/**
 * Object pooling. ARCHITECTURE.md N9: no per-frame allocation in hot paths.
 * A GC spike is a p99 failure, and p99 is the gate.
 */

export class Pool {
  /**
   * @param {() => T} factory      builds a fresh instance
   * @param {(o:T) => void} reset  returns an instance to a clean state
   * @param {number} initial       preallocate this many
   * @param {number} max           hard cap; acquire() returns null past it
   */
  constructor(factory, reset, initial = 0, max = Infinity) {
    this._factory = factory;
    this._reset = reset || (() => {});
    this._free = [];
    this._max = max;
    this.created = 0;
    this.live = 0;
    this.peak = 0;
    this.starved = 0;
    for (let i = 0; i < initial; i++) { this._free.push(factory()); this.created++; }
  }

  acquire() {
    let o = this._free.pop();
    if (!o) {
      if (this.created >= this._max) { this.starved++; return null; }
      o = this._factory();
      this.created++;
    }
    this.live++;
    if (this.live > this.peak) this.peak = this.live;
    return o;
  }

  release(o) {
    if (!o) return;
    this._reset(o);
    this._free.push(o);
    this.live--;
  }

  stats() {
    return { created: this.created, live: this.live, free: this._free.length, peak: this.peak, starved: this.starved };
  }
}

/**
 * Fixed-capacity ring of active items with swap-remove. The standard shape for particles,
 * pop-texts, tyre marks — anything with a lifetime.
 *
 * Usage:
 *   const list = new ActiveList(2048, () => ({x:0,y:0,life:0}));
 *   const p = list.spawn(); if (p) { p.x = ...; }
 *   list.update((p, i) => { p.life -= dt; return p.life > 0; });   // return false to retire
 */
export class ActiveList {
  constructor(capacity, factory) {
    this.capacity = capacity;
    this.items = new Array(capacity);
    for (let i = 0; i < capacity; i++) this.items[i] = factory(i);
    this.count = 0;
    this.dropped = 0;
  }

  spawn() {
    if (this.count >= this.capacity) { this.dropped++; return null; }
    return this.items[this.count++];
  }

  /** Retire index i by swapping the last active item into its slot. O(1). */
  retire(i) {
    const last = --this.count;
    if (i !== last) {
      const t = this.items[i];
      this.items[i] = this.items[last];
      this.items[last] = t;
    }
  }

  /** fn returns true to keep, false to retire. Iterates backwards so swap-remove is safe. */
  update(fn) {
    for (let i = this.count - 1; i >= 0; i--) {
      if (!fn(this.items[i], i)) this.retire(i);
    }
  }

  forEach(fn) {
    for (let i = 0; i < this.count; i++) fn(this.items[i], i);
  }

  clear() { this.count = 0; }
}

/**
 * Scratch vectors. Import and reuse instead of `new THREE.Vector3()` in a loop.
 * Each subsystem should take its own slice by convention (see comments) to avoid two
 * systems stomping the same scratch across a call boundary.
 */
export function makeScratch(Ctor, n) {
  const arr = new Array(n);
  for (let i = 0; i < n; i++) arr[i] = new Ctor();
  let cursor = 0;
  return {
    /** Get the next scratch instance. Valid only until you call reset(). */
    next() { const v = arr[cursor]; cursor = (cursor + 1) % n; return v; },
    reset() { cursor = 0; },
    all: arr,
  };
}
